import { useEffect, useState } from "react";
import api from "../api/axiosInstance";
import { toast } from "react-hot-toast";
import {
  CalendarDays,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import BookingCalendar from "./BookingCalendar";

const extractErrorMessage = (err) =>
  err.response?.data?.errors?.[0] ||
  err.response?.data?.message ||
  "Something went wrong, please try again.";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BookingRequestForm({ room }) {
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [form, setForm] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    numberOfGuests: "",
    specialRequests: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!room?.id) return;
    const fetchBlockedDates = async () => {
      try {
        const res = await api.get(`/room-bookings/blocked-dates/${room.id}`);
        setBlockedRanges(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchBlockedDates();
  }, [room?.id]);

  if (!room.available) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Currently Unavailable
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          This room isn't open for new bookings right now (for example, it
          may be under renovation). Please check back later, or contact us
          directly to ask about other available rooms.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-emerald-100 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Request Received
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Thank you - we've received your booking request for{" "}
          <span className="font-semibold">{room.title}</span>. Our team will
          contact you by phone or email shortly to confirm your stay.
        </p>
      </div>
    );
  }

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const next = {};
    if (!form.customerName.trim()) next.customerName = "Name is required";
    if (!form.customerEmail.trim()) next.customerEmail = "Email is required";
    else if (!EMAIL_RE.test(form.customerEmail.trim()))
      next.customerEmail = "Enter a valid email address";
    if (!form.customerPhone.trim()) next.customerPhone = "Phone number is required";
    if (!checkIn || !checkOut)
      next.dates = "Select a check-in and check-out date";
    else if (checkOut <= checkIn)
      next.dates = "Check-out date must be after check-in date";
    const guests = Number(form.numberOfGuests);
    if (!guests || guests < 1) next.numberOfGuests = "At least 1 guest is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await api.post("/room-bookings/submit", {
        roomId: room.id,
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim(),
        checkInDate: checkIn,
        checkOutDate: checkOut,
        numberOfGuests: Number(form.numberOfGuests),
        specialRequests: form.specialRequests.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-emerald-100">
      <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-emerald-600" />
        Request to Book
      </h3>
      <p className="text-sm text-gray-500 mb-6">
        Pick your dates below, then submit your details - our team will
        follow up to confirm.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <BookingCalendar
            blockedRanges={blockedRanges}
            checkIn={checkIn}
            checkOut={checkOut}
            onChange={(newCheckIn, newCheckOut) => {
              setCheckIn(newCheckIn);
              setCheckOut(newCheckOut);
            }}
          />
          <div className="mt-2 text-sm text-gray-600">
            {checkIn && checkOut ? (
              <span>
                <span className="font-semibold">{checkIn}</span> &rarr;{" "}
                <span className="font-semibold">{checkOut}</span>
              </span>
            ) : checkIn ? (
              <span>Now pick a check-out date</span>
            ) : (
              <span>Pick a check-in date to start</span>
            )}
          </div>
          {errors.dates && (
            <p className="text-red-600 text-sm mt-1">{errors.dates}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <input
              name="customerName"
              value={form.customerName}
              onChange={handleFieldChange}
              placeholder="Full Name"
              className="w-full border rounded-lg p-2.5 text-sm"
            />
            {errors.customerName && (
              <p className="text-red-600 text-xs mt-1">{errors.customerName}</p>
            )}
          </div>

          <div>
            <input
              name="customerEmail"
              type="email"
              value={form.customerEmail}
              onChange={handleFieldChange}
              placeholder="Email Address"
              className="w-full border rounded-lg p-2.5 text-sm"
            />
            {errors.customerEmail && (
              <p className="text-red-600 text-xs mt-1">{errors.customerEmail}</p>
            )}
          </div>

          <div>
            <input
              name="customerPhone"
              value={form.customerPhone}
              onChange={handleFieldChange}
              placeholder="Phone Number"
              className="w-full border rounded-lg p-2.5 text-sm"
            />
            {errors.customerPhone && (
              <p className="text-red-600 text-xs mt-1">{errors.customerPhone}</p>
            )}
          </div>

          <div>
            <input
              name="numberOfGuests"
              type="number"
              min="1"
              max={room.capacity || undefined}
              value={form.numberOfGuests}
              onChange={handleFieldChange}
              placeholder="Number of Guests"
              className="w-full border rounded-lg p-2.5 text-sm"
            />
            {room.capacity && (
              <p className="text-xs text-gray-400 mt-1">
                This room sleeps up to {room.capacity} guests
              </p>
            )}
            {errors.numberOfGuests && (
              <p className="text-red-600 text-xs mt-1">{errors.numberOfGuests}</p>
            )}
          </div>

          <textarea
            name="specialRequests"
            value={form.specialRequests}
            onChange={handleFieldChange}
            placeholder="Special requests (optional)"
            rows="2"
            className="w-full border rounded-lg p-2.5 text-sm"
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-bold hover:from-emerald-600 hover:to-lime-600 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Submitting..." : "Submit Booking Request"}
          </button>
        </form>
      </div>
    </div>
  );
}
